import {
  AfterContentChecked,
  Component,
  OnInit,
  OnDestroy,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { DetailsContainer } from 'src/app/core/containers/details-container';
import { dynamicSubfieldSort } from 'src/app/core/functions/common-functions';
import { DirtyStatus } from 'src/app/model/common/dirty-status';
import { ShiftTurnover } from 'src/app/model/shift/shift-turnover';
import { LogAndMessageService } from 'src/app/services/log-and-message.service';
import { ShiftTurnoverApiService } from 'src/app/services/api/shift-turnover-api.service';
import { ShiftTurnoverBuilderService } from 'src/app/services/builder/shift-turnover-builder.service';
import { isNullOrUndefined } from 'src/app/utils/utils';
import { Column } from 'src/app/model/common/column';
import { MOCEvent } from 'src/app/model/crm/moc-event';
import { MocEventApiService } from 'src/app/services/api/moc-event-api.service';
import { MocEventBuilderService } from 'src/app/services/builder/moc-event-builder.service';
import { CrmShiftTurnoverTableService } from 'src/app/services/table/crm-shift-turnover-table.service';
import { getUserTimezone } from 'src/app/core/functions/date-functions';
import { ProgressSpinnerComponent } from '../../../core/components/progress-spinner.component';
import { ButtonDirective } from '@openng/optimus-ui/button';
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { Panel } from '@openng/optimus-ui/panel';
import { DatePipe } from '@angular/common';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-crm-shift-turnover-print-container',
  templateUrl: './crm-shift-turnover-print-container.component.html',
  styleUrls: ['./crm-shift-turnover-print-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ProgressSpinnerComponent,
    ButtonDirective,
    TranslateDirective,
    Panel,
    DatePipe,
    TranslatePipe,
  ],
})
export class CrmShiftTurnoverPrintContainerComponent
  extends DetailsContainer
  implements OnInit, OnDestroy, AfterContentChecked
{
  protected _logAndMessage = inject(LogAndMessageService);
  protected _table = inject(CrmShiftTurnoverTableService);
  protected _builder = inject(ShiftTurnoverBuilderService);
  protected _api = inject(ShiftTurnoverApiService);
  protected _mocApi = inject(MocEventApiService);
  protected _mocBuilder = inject(MocEventBuilderService);

  shiftTurnover: ShiftTurnover | null = null;
  incomingAnalyst: string | null = null;
  departingAnalyst: string | null = null;
  statusValue: string = '';
  detailsObj: any;
  itemsArray: any[] = [];
  mitArray: any[] = [];
  mocColumns: Column[] = [];
  unacknowledged: MOCEvent[] = [];
  acknowledgedAndUpcoming: MOCEvent[] = [];
  timezone: string | null = null;

  ngOnInit() {
    this.timezone = getUserTimezone();
    this.setupTabClosingSubscription();
    this.initialize();
  }

  ngOnDestroy(): void {
    if (this.tabClosingSub) {
      this.tabClosingSub.unsubscribe();
    }
  }

  ngAfterContentChecked(): void {
    const activeId = this.id;
    if (
      !isNullOrUndefined(activeId) &&
      this.shiftTurnover &&
      this.shiftTurnover.id !== activeId &&
      !this.doCheckComplete
    ) {
      this.doCheckComplete = true;
      this._table.setSelected(null);
      this.initialize();
    }
  }

  initialize() {
    const id = this.id;

    if (!id) {
      this.shiftTurnover = this._table.getSelected();
      // no id and nothing cached, go back to search.
      if (!this.shiftTurnover) {
        this._logAndMessage.errorLogOnly('CRM Shift turnover was null');
        const index = this._tabService.getActiveIndex();
        this._tabService.closeTab(index);
      }
      this.setupScreen();
    } else {
      this._api.getShiftTurnover(id).subscribe((turnover) => {
        if (turnover) {
          this.shiftTurnover = turnover;
          this.doCheckComplete = false;
          this.setupScreen();
        } else {
          // nothing found, go back to search.
          this._logAndMessage.errorLogOnly(
            'Did not find shift turnover for id: ' + id
          );
          const index = this._tabService.getActiveIndex();
          this._tabService.closeTab(index);
        }
      });
    }
  }
  loadMocEvents(): void {
    if (this.shiftTurnover && this.shiftTurnover.AOR) {
      this._mocApi
        .getMOCEventsForAOR(
          this.shiftTurnover.AOR.id,
          this.shiftTurnover.incomingCompletedDate
        )
        .subscribe({
          next: (mocs) => {
            if (this.shiftTurnover && this.shiftTurnover.ShiftDetails) {
              const inArray = this.shiftTurnover.ShiftDetails.filter(
                (d) => d.incomingDepartingFlag === 'I'
              );
              if (!this.shiftTurnover.incomingCompletedDate) {
                const mocEvents = this._mocBuilder.buildMocEventsForAOR(
                  mocs,
                  inArray[0].Analyst!.id,
                  new Date(this.shiftTurnover.shiftDate)
                );
                this.unacknowledged = [...mocEvents.unacknowledged];
                this.acknowledgedAndUpcoming = [...mocEvents.acknowledged];
              } else {
                this.acknowledgedAndUpcoming =
                  this._mocBuilder.buildHistoricalAcknowledgements(
                    mocs,
                    new Date(this.shiftTurnover.incomingCompletedDate)
                  );
              }
            }
            this.acknowledgedAndUpcoming.forEach(
              (moc) =>
                (moc.MOCEventAcknowledgements =
                  moc.MOCEventAcknowledgements.filter(
                    (ack) =>
                      this.incomingAnalyst ===
                      ack.Contact.firstName + ' ' + ack.Contact.lastName
                  ))
            );
          },
          error: (error) => {
            this._logAndMessage.errorLogOnly(error);
            this._logAndMessage.translateToErrorAlert({
              bodyKey: 'CRM.MESSAGES.ERROR.GET_MOC_EVENTS',
              headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
            });
          },
        });
    }
  }

  async setupScreen() {
    const printLabel = await firstValueFrom(
      this._translateService.get('COMMON.LABEL.PRINT')
    );
    const header =
      printLabel +
      ': ' +
      this.shiftTurnover?.shift +
      ' - ' +
      this.shiftTurnover?.shiftDate.toISOString();
    this._tabService.updateActiveTabLabel(header);
    const inArray = this.shiftTurnover?.ShiftDetails.filter(
      (d) => d.incomingDepartingFlag === 'I'
    );
    const depArray = this.shiftTurnover?.ShiftDetails.filter(
      (d) => d.incomingDepartingFlag === 'D'
    );
    this.statusValue = this._builder.getStatusLabelForStatus(
      this.shiftTurnover!.status
    );

    if (inArray) {
      this.incomingAnalyst =
        inArray.length > 0
          ? inArray[0].Analyst!.firstName + ' ' + inArray[0].Analyst!.lastName
          : null;
    }

    if (depArray) {
      this.departingAnalyst =
        depArray.length > 0
          ? depArray[0].Analyst!.firstName + ' ' + depArray[0].Analyst!.lastName
          : null;
    }
    this.loadMocEvents();
    let incoming,
      departing = null;
    if (
      this.shiftTurnover &&
      this.shiftTurnover.ShiftDetails &&
      this.shiftTurnover.ShiftDetails.length > 0
    ) {
      incoming = this.shiftTurnover.ShiftDetails.filter(
        (d) => d.incomingDepartingFlag === 'I'
      )[0];
      departing = this.shiftTurnover.ShiftDetails.filter(
        (d) => d.incomingDepartingFlag === 'D'
      )[0];
      this.detailsObj = {
        incomingAdditionalComments: incoming.additionalComments,
        incomingFitFlag: incoming.fitFlag,
        incomingScadaFlag: incoming.scadaFlag,
        incomingReviewedAllSections: incoming.reviewedAllSections,
        departingAdditionalComments: departing.additionalComments,
        departingFitFlag: departing.fitFlag,
        departingScadaFlag: departing.scadaFlag,
      };

      const depItemArray = departing.ShiftTurnoverItems.filter(
        (co: any) => co.dirtyStatus !== DirtyStatus.DELETED
      ).sort(dynamicSubfieldSort('ShiftItem', 'sortOrder', 1));

      this.itemsArray = [];
      for (let i = 0; i < depItemArray.length; i++) {
        const departingItem = depItemArray[i];
        this.itemsArray.push({
          incomingId: null,
          departingId: departingItem.id,
          itemName: departingItem.ShiftItem?.name,
          incomingShiftItemId: null,
          departingShiftItemId: departingItem.ShiftItem?.id,
          incomingComments: null,
          departingComments: departingItem.comments,
          incomingTurnoverFlag: null,
          departingTurnoverFlag: departingItem.turnoverFlag,
        });
      }

      const depMitArray = departing.ShiftTurnoverMitigations.filter(
        (co: any) => co.dirtyStatus !== DirtyStatus.DELETED
      ).sort(dynamicSubfieldSort('ShiftMitigation', 'sortOrder', 1));
      this.mitArray = [];
      for (let j = 0; j < depMitArray.length; j++) {
        const mit = depMitArray[j];
        this.mitArray.push({
          id: mit.id,
          mitigationId: mit.ShiftMitigation?.id,
          mitigationName: mit.ShiftMitigation?.name,
          turnoverFlag: mit.turnoverFlag,
        });
      }
    }
  }

  print() {
    // This used to use printjs, but we ran into some issues with lines
    // overlapping each other in the migration to angular 13, so now
    // we use @media print styling to only show what is needed
    // and use the default window.print() to generate the document!
    window.print();
  }
}
